import React, { useEffect, useState } from 'react';

let editorNodeCounter = 1;

function ensureEditorId(entity, prefix) {
  if (entity?._editorId) return entity;
  return { ...entity, _editorId: `${prefix}-${editorNodeCounter++}` };
}

function normalizeMenu(menu) {
  let changed = false;

  const categories = (menu.categories || []).map(category => {
    const nextCategory = ensureEditorId(category, 'cat');
    if (nextCategory !== category) changed = true;

    const items = (nextCategory.items || []).map(item => {
      const nextItem = ensureEditorId(item, 'item');
      if (nextItem !== item) changed = true;
      return nextItem;
    });

    if (items !== nextCategory.items) {
      return { ...nextCategory, items };
    }

    return nextCategory;
  });

  return changed ? { ...menu, categories } : menu;
}

export default function MenuEditor({ menu, onChange }) {
  const [editingItem, setEditingItem] = useState(null); // { catId, itemId }

  useEffect(() => {
    const normalizedMenu = normalizeMenu(menu);
    if (normalizedMenu !== menu) {
      onChange(normalizedMenu);
    }
  }, [menu, onChange]);

  const updateCategory = (catIdx, updates) => {
    const next = { ...menu };
    next.categories = [...menu.categories];
    next.categories[catIdx] = { ...next.categories[catIdx], ...updates };
    onChange(next);
  };

  const removeCategory = (catIdx) => {
    const next = { ...menu };
    next.categories = menu.categories.filter((_, i) => i !== catIdx);
    onChange(next);
  };

  const addCategory = () => {
    const next = { ...menu };
    next.categories = [
      ...menu.categories,
      ensureEditorId({ name: 'New Category', items: [] }, 'cat')
    ];
    onChange(next);
  };

  const moveCategory = (catIdx, direction) => {
    const newIdx = catIdx + direction;
    if (newIdx < 0 || newIdx >= menu.categories.length) return;

    const nextCategories = [...menu.categories];
    [nextCategories[catIdx], nextCategories[newIdx]] = [nextCategories[newIdx], nextCategories[catIdx]];
    onChange({ ...menu, categories: nextCategories });
  };

  const updateItem = (catIdx, itemIdx, updates) => {
    const next = { ...menu };
    next.categories = [...menu.categories];
    const cat = { ...next.categories[catIdx] };
    cat.items = [...cat.items];
    cat.items[itemIdx] = { ...cat.items[itemIdx], ...updates };
    next.categories[catIdx] = cat;
    onChange(next);
  };

  const removeItem = (catIdx, itemIdx) => {
    const next = { ...menu };
    next.categories = [...menu.categories];
    const cat = { ...next.categories[catIdx] };
    cat.items = cat.items.filter((_, i) => i !== itemIdx);
    next.categories[catIdx] = cat;
    onChange(next);
  };

  const addItem = (catIdx) => {
    const next = { ...menu };
    next.categories = [...menu.categories];
    const cat = { ...next.categories[catIdx] };
    const nextItem = ensureEditorId({ name: '', description: '', price: '' }, 'item');
    cat.items = [...cat.items, nextItem];
    next.categories[catIdx] = cat;
    onChange(next);
    setEditingItem({ catId: cat._editorId, itemId: nextItem._editorId });
  };

  const moveItem = (catIdx, itemIdx, direction) => {
    const category = menu.categories[catIdx];
    const newIdx = itemIdx + direction;
    const nextCategories = [...menu.categories];

    if (newIdx >= 0 && newIdx < category.items.length) {
      const nextCategory = { ...category, items: [...category.items] };
      [nextCategory.items[itemIdx], nextCategory.items[newIdx]] = [nextCategory.items[newIdx], nextCategory.items[itemIdx]];
      nextCategories[catIdx] = nextCategory;
      onChange({ ...menu, categories: nextCategories });
      return;
    }

    const targetCatIdx = catIdx + direction;
    if (targetCatIdx < 0 || targetCatIdx >= menu.categories.length) return;

    const sourceCategory = { ...category, items: [...category.items] };
    const targetCategory = { ...menu.categories[targetCatIdx], items: [...menu.categories[targetCatIdx].items] };
    const [itemToMove] = sourceCategory.items.splice(itemIdx, 1);

    if (direction < 0) {
      targetCategory.items.push(itemToMove);
    } else {
      targetCategory.items.unshift(itemToMove);
    }

    nextCategories[catIdx] = sourceCategory;
    nextCategories[targetCatIdx] = targetCategory;
    onChange({ ...menu, categories: nextCategories });
  };

  const moveItemToCategory = (catIdx, itemIdx, targetCatId) => {
    const targetCatIdx = menu.categories.findIndex(category => category._editorId === targetCatId);
    if (targetCatIdx < 0 || targetCatIdx === catIdx) return;

    const nextCategories = [...menu.categories];
    const sourceCategory = { ...menu.categories[catIdx], items: [...menu.categories[catIdx].items] };
    const targetCategory = { ...menu.categories[targetCatIdx], items: [...menu.categories[targetCatIdx].items] };
    const [itemToMove] = sourceCategory.items.splice(itemIdx, 1);

    targetCategory.items.push(itemToMove);
    nextCategories[catIdx] = sourceCategory;
    nextCategories[targetCatIdx] = targetCategory;

    onChange({ ...menu, categories: nextCategories });
    setEditingItem({ catId: targetCategory._editorId, itemId: itemToMove._editorId });
  };

  const isFirstMenuItem = (catIdx, itemIdx) => catIdx === 0 && itemIdx === 0;
  const isLastMenuItem = (catIdx, itemIdx) =>
    catIdx === menu.categories.length - 1 && itemIdx === menu.categories[catIdx].items.length - 1;

  const isEditing = (cat, item) =>
    editingItem &&
    editingItem.catId === cat._editorId &&
    editingItem.itemId === item._editorId;

  return (
    <div className="menu-editor">
      {menu.categories.length === 0 && (
        <div className="editor-empty">
          <p>No categories yet. Go back and add menu sources, or add a category manually.</p>
        </div>
      )}

      {menu.categories.map((cat, catIdx) => (
        <div key={cat._editorId || `cat-${catIdx}`} className="editor-category-shell">
          <div className="editor-reorder-rail" aria-label="Category order controls">
            <button
              type="button"
              className="move-btn move-btn-card"
              onClick={() => moveCategory(catIdx, -1)}
              disabled={catIdx === 0}
              title="Move category up"
            >
              ↑
            </button>
            <button
              type="button"
              className="move-btn move-btn-card"
              onClick={() => moveCategory(catIdx, 1)}
              disabled={catIdx === menu.categories.length - 1}
              title="Move category down"
            >
              ↓
            </button>
          </div>

          <div className="editor-category">
            <div className="editor-cat-header">
              <input
                className="editor-cat-name"
                value={cat.name}
                onChange={e => updateCategory(catIdx, { name: e.target.value })}
                placeholder="Category Name"
              />

              <button
                type="button"
                className="editor-delete-btn"
                onClick={() => removeCategory(catIdx)}
                title="Delete category"
              >
                🗑️
              </button>
            </div>

            <div className="editor-items">
              {cat.items.map((item, itemIdx) => (
                <div
                  key={item._editorId || `item-${catIdx}-${itemIdx}`}
                  className="editor-item-shell"
                >
                  <div className="editor-item-rail" aria-label="Item order controls">
                    <button
                      type="button"
                      className="move-btn"
                      onClick={() => moveItem(catIdx, itemIdx, -1)}
                      disabled={isFirstMenuItem(catIdx, itemIdx)}
                      title="Move item up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="move-btn"
                      onClick={() => moveItem(catIdx, itemIdx, 1)}
                      disabled={isLastMenuItem(catIdx, itemIdx)}
                      title="Move item down"
                    >
                      ↓
                    </button>
                  </div>

                  <div className={`editor-item ${isEditing(cat, item) ? 'editing' : ''}`}>
                    {isEditing(cat, item) ? (
                      <div className="item-edit-form">
                        <div className="item-edit-row">
                          <input
                            className="item-edit-input name"
                            value={item.name}
                            onChange={e => updateItem(catIdx, itemIdx, { name: e.target.value })}
                            placeholder="Item name"
                            autoFocus
                          />
                          <input
                            className="item-edit-input price"
                            value={item.price}
                            onChange={e => updateItem(catIdx, itemIdx, { price: e.target.value })}
                            placeholder="$0.00"
                          />
                        </div>
                        <textarea
                          className="item-edit-desc"
                          value={item.description}
                          onChange={e => updateItem(catIdx, itemIdx, { description: e.target.value })}
                          placeholder="Description (optional)"
                          rows={2}
                        />
                        {menu.categories.length > 1 && (
                          <label className="item-category-control">
                            <span>Move to divider</span>
                            <select
                              value={cat._editorId}
                              onChange={e => moveItemToCategory(catIdx, itemIdx, e.target.value)}
                            >
                              {menu.categories.map(category => (
                                <option key={category._editorId} value={category._editorId}>
                                  {category.name || 'Untitled divider'}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        <div className="item-edit-actions">
                          <button type="button" className="item-done-btn" onClick={() => setEditingItem(null)}>
                            Done
                          </button>
                          <button
                            type="button"
                            className="item-delete-btn"
                            onClick={() => {
                              removeItem(catIdx, itemIdx);
                              setEditingItem(null);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="item-row-container"
                        onClick={() => setEditingItem({ catId: cat._editorId, itemId: item._editorId })}
                        style={{ flex: 1, cursor: 'pointer', padding: '8px 0' }}
                      >
                        <div className="item-row" style={{ margin: 0, padding: 0 }}>
                          <span className="item-name">{item.name || '(untitled)'}</span>
                          <span className="item-dots"></span>
                          <span className="item-price">{item.price || '—'}</span>
                          <button
                            type="button"
                            className="item-remove-inline"
                            onClick={e => {
                              e.stopPropagation();
                              removeItem(catIdx, itemIdx);
                            }}
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                        {item.description && (
                          <div className="item-desc-preview" style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px', paddingRight: '24px' }}>
                            {item.description}
                          </div>
                        )}
                        {menu.categories.length > 1 && (
                          <select
                            className="item-category-select"
                            value={cat._editorId}
                            onClick={e => e.stopPropagation()}
                            onChange={e => {
                              e.stopPropagation();
                              moveItemToCategory(catIdx, itemIdx, e.target.value);
                            }}
                            aria-label={`Move ${item.name || 'item'} to divider`}
                          >
                            {menu.categories.map(category => (
                              <option key={category._editorId} value={category._editorId}>
                                {category.name || 'Untitled divider'}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className="editor-add-item" onClick={() => addItem(catIdx)}>
              + Add Item
            </button>
          </div>
        </div>
      ))}

      <button type="button" className="editor-add-category" onClick={addCategory}>
        + Add Category
      </button>
    </div>
  );
}
